<template lang="pug">
	.app-core--rrule-editor.flex-col.space-large
		.flex-col.space-medium
			.flex-row.space-medium
				.flex-col
					.font-small.upper.grey {{$tCaption('Repeat')}}
					DxSelectBox(:data-source="repeatTypes", valueExpr="id", displayExpr="text", :value.sync="repeatPeriod", styling-mode="filled", :width="130")
				.flex-col
					.font-small.upper.grey {{$tCaption('Every')}}
					.flex-row.space.align-center
						DxNumberBox(:value.sync="interval", :width="80", styling-mode="filled", :showSpinButtons="true")
						.grey {{repeatType.intervalCaption}}
			.flex-col(v-if="hasByDay")
				.font-small.upper.grey {{$tCaption('On')}}
				.flex-row.align-center.font-small
					.adt-tab.no-borders.flex-row.align-center.upper(v-for="wd in weekDays", @click="$Data.toggleSelectedIdInArray(wd.id, byDay)", :class="{ active: byDay.includes(wd.id) }") {{wd.text}}
			.flex-col(v-if="hasByMonth || hasByMonthDay")
				.font-small.upper.grey {{$tCaption('On')}}
				.flex-row.space-medium.align-center
					.flex-col(v-if="hasByMonth")
						DxSelectBox(:data-source="months", valueExpr="id", displayExpr="text", :value.sync="byMonth", styling-mode="filled", :width="130")
					.flex-col(v-if="hasByMonthDay")
						.flex-row.align-center.font-small
							DxNumberBox(:value.sync="byMonthDay", :width="80", styling-mode="filled", :min="1", :max="31", :showSpinButtons="true")
		.flex-col.space
			.font-small.upper.grey {{$tCaption('End repeat')}}
			.flex-col.space
				div(@click="endRepeatPeriod = 'NEVER'")
					DxCheckBox(:text="$tCaption('Never')", :value="endRepeatPeriod === 'NEVER'")
				.flex-row.space-medium.align-center
					div(@click="endRepeatPeriod = 'UNTIL'")
						DxCheckBox(:text="$tCaption('On')", :value="endRepeatPeriod === 'UNTIL'")
					DxDateBox(:value.sync="until", :disabled="endRepeatPeriod !== 'UNTIL'", :width="200", type="datetime")
				.flex-row.space-medium.align-center
					div(@click="endRepeatPeriod = 'COUNT'")
						DxCheckBox(:text="$tCaption('After')", :value="endRepeatPeriod === 'COUNT'")
					DxNumberBox(:value.sync="count", :width="60", :disabled="endRepeatPeriod !== 'COUNT'", :showSpinButtons="true")
					span {{$tCaption('occurrence(s)')}}
</template>

<script>

import VueTypes from 'vue-types';
import DxSelectBox from 'devextreme-vue/select-box';
import DxNumberBox from 'devextreme-vue/number-box';
import DxCheckBox from 'devextreme-vue/check-box';
import DxDateBox from 'devextreme-vue/date-box';

export default {
	name: 'app-core--rrule-editor',
	translationCategory: 'Scheduling',
	props: {
		value: VueTypes.string.def(null)
	},
	components: { DxSelectBox, DxNumberBox, DxCheckBox, DxDateBox },
	data() {
		const repeatTypes = [
			{ id: 'HOURLY', text: this.$tCaption('Hourly'), intervalCaption: this.$tCaption('hour(s)') },
			{ id: 'DAILY', text: this.$tCaption('Daily'), intervalCaption: this.$tCaption('day(s)') },
			{ id: 'WEEKLY', text: this.$tCaption('Weekly'), intervalCaption: this.$tCaption('week(s)') },
			{ id: 'MONTHLY', text: this.$tCaption('Monthly'), intervalCaption: this.$tCaption('month(s)') },
			{ id: 'YEARLY', text: this.$tCaption('Yearly'), intervalCaption: this.$tCaption('year(s)') },
		];

		const weekDays = [
			{ id: 'MO', text: this.$tCaption('Mon') },
			{ id: 'TU', text: this.$tCaption('Tue') },
			{ id: 'WE', text: this.$tCaption('Wed') },
			{ id: 'TH', text: this.$tCaption('Thu') },
			{ id: 'FR', text: this.$tCaption('Fri') },
			{ id: 'SA', text: this.$tCaption('Sat') },
			{ id: 'SU', text: this.$tCaption('Sun') },
		];

		const months = [
			this.$tCaption('January'),
			this.$tCaption('February'),
			this.$tCaption('March'),
			this.$tCaption('April'),
			this.$tCaption('May'),
			this.$tCaption('June'),
			this.$tCaption('July'),
			this.$tCaption('August'),
			this.$tCaption('September'),
			this.$tCaption('October'),
			this.$tCaption('November'),
			this.$tCaption('December'),
		].map((f, idx) => ({ id: idx + 1, text: f }));

		return {
			repeatTypes,
			repeatEnabled: false,
			repeatPeriod: 'DAILY',
			endRepeatPeriod: 'NEVER',
			count: 1,
			until: this.$moment(new Date()).endOf('day').toDate(),
			interval: 1,
			weekDays,
			byDay: ['MO'],
			months,
			byMonthDay: (new Date()).getDate(),
			byMonth: (new Date()).getMonth() + 1
		}
	},
	computed: {
		repeatType() {
			return this.repeatTypes.find(f => f.id === this.repeatPeriod);
		},
		hasByDay() {
			return this.repeatPeriod === 'WEEKLY';
		},
		hasByMonthDay() {
			return this.repeatPeriod === 'MONTHLY' || this.repeatPeriod === 'YEARLY';
		},
		hasByMonth() {
			return this.repeatPeriod === 'YEARLY';
		},
		rrule() {
			return this.getAsString();
		}
	},
	watch: {
		value(v) {
			if (this.rrule !== (v || '')) {
				this.parseString(this.value);
			}
		},
		byDay() {
			if (!this.byDay.length) {
				this.byDay.push('MO');
			}
		},
		rrule() {
			this.$emit('change', this.rrule);
			this.$emit('update:value', this.rrule);
		}
	},
	mounted() {
		if (__DEBUG__)
			window.rruleEditor = this;
		if (this.value) {
			this.parseString(this.value);
		}
	},
	methods: {
		getAsString() {
			const params = [];
			params.push(`FREQ=${this.repeatPeriod}`);

			if (this.hasByDay) {
				params.push(`BYDAY=${this.byDay.join(',')}`);
			}

			if (this.hasByMonthDay) {
				params.push(`BYMONTHDAY=${this.byMonthDay}`);
			}

			if (this.hasByMonth) {
				params.push(`BYMONTH=${this.byMonth}`);
			}

			if (this.endRepeatPeriod === 'COUNT') {
				params.push(`COUNT=${this.count}`);
			}

			if (this.endRepeatPeriod === 'UNTIL') {
				params.push(`UNTIL=${this.$moment(this.until).utc().format('YYYYMMDDTHHmmss[Z]')}`);
			}

			if (this.interval > 1) {
				params.push(`INTERVAL=${this.interval}`);
			}

			return params.join(';');
		},
		parseString(str) {
			if (!str) return;

			const v = str.split(';').filter(f => f).reduce((res, item) =>{
				const splitted = item.split('=');
				res[splitted[0]] = isNaN(splitted[1]) ? splitted[1] : parseInt(splitted[1]);
				return res;
			} , {});

			this.repeatPeriod = v['FREQ'] || this.repeatPeriod;
			this.byMonthDay = v['BYMONTHDAY'] || this.byMonthDay;
			this.byMonth = v['BYMONTH'] || this.byMonth;
			this.interval = v['INTERVAL'] || this.interval;

			if (v['BYDAY'])
				this.byDay = v['BYDAY'].split(',').filter(f => f);
			
			if (v['UNTIL']) {
				this.endRepeatPeriod = 'UNTIL';
				this.until = this.$moment(v['UNTIL']).toDate();
			} else if (v['COUNT'])
				this.endRepeatPeriod = 'COUNT';
			else
				this.endRepeatPeriod = 'NEVER';
		}
	}
};

</script>

<style lang="scss">
	.app-core--rrule-editor {
		
	}
</style>
